import React, { useState, useEffect, useRef } from 'react';
import { parseContactsCsv, CSV_MAX_FILE_BYTES, CSV_MAX_ROWS, CSV_TEMPLATE } from '../../lib/csvContacts';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Users, 
  UserPlus, 
  Search, 
  Phone, 
  Mail, 
  MessageSquare,
  Upload,
  Download,
  Video,
  Send
} from 'lucide-react';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useToast } from '../../hooks/use-toast';
import FriendInvitation from '../Common/FriendInvitation';
import StaffChatModal from '../WebRTC/StaffChatModal';
import { auth } from '../../lib/firebase';

const ContactManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [importText, setImportText] = useState('');
  // D14 CSV import: single canonical parser feeds both file + paste. Honest phased progress.
  const [importMode, setImportMode] = useState('file');      // 'file' | 'paste'
  const [importPreview, setImportPreview] = useState(null);   // parser result or null
  const [importPhase, setImportPhase] = useState('idle');     // idle|reading|validating|ready|importing|complete|issues|failed
  const [importResult, setImportResult] = useState(null);     // server summary
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [staffChatPeer, setStaffChatPeer] = useState(null);  // same-business staff DM target (real chat)

  const { user, makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();

  // Contact action handlers
  const handleStartConversation = (contact) => {
    try {
      // Exact portal match — see note in handleCallContact ('/business/customers' contains 'customer').
      const isCustomerContext = location.pathname.startsWith('/customer');

      if (isCustomerContext) {
        toast({
          title: "Opening Chat",
          description: `Starting chat with ${contact.display_name || contact.first_name}...`,
        });
        
        navigate('/customer/peer-chat', {
          state: { peerChatContact: contact }
        });
      } else {
        // WEBCRM-CI-CHAT-ICON-WRONG-WORKFLOW fix: previously this chat icon claimed "Opening Chat"
        // but actually navigated to the Create-New-Ticket workflow (/business/tickets) — the wrong
        // action for a chat control. The REAL business-plane direct chat is staff-to-staff
        // (StaffChatModal → /api/v1/staff-chat/*), the same one Voice Calls uses. So for a contact
        // the backend resolved to a same-business Servisfy staff member (is_app_user + app_user_id),
        // open that real chat by the staff firebase_uid. External customers/leads have no direct-chat
        // workflow → honest unavailable (no fake, no ticket misroute).
        if (contact.is_app_user && contact.app_user_id) {
          setStaffChatPeer({
            id: contact.app_user_id,
            name: contact.display_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Staff',
            role: contact.role || ''
          });
          return;
        }
        toast({
          title: "Direct chat not available",
          description: "Direct chat is available only for same-business Servisfy staff. To start a customer conversation, create a ticket.",
        });
      }

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to start conversation",
        variant: "destructive",
      });
    }
  };

  // Scope (Option 1 — same-business staff only): WebRTC connects peers within the SAME business.
  // A contact is callable ONLY if the backend resolved it to a logged-in staff member of this brand
  // (is_app_user + app_user_id). We call by app_user_id (the WebRTC identity), never the raw phone.
  // External numbers / customer-plane app users are NOT callable yet — we say so honestly (no fake
  // call, no login redirect). PSTN + cross-plane are deferred backlog items.
  const startContactCall = (contact, isVideoCall) => {
    const contactName = contact.display_name || contact.first_name || 'contact';

    if (!(contact.is_app_user && contact.app_user_id)) {
      toast({
        title: isVideoCall ? "Video Call Not Available" : "Call Not Available",
        description: "This contact is not currently callable by WebRTC. External PSTN / customer-plane calling is coming in a later release.",
      });
      return;
    }

    try {
      toast({
        title: isVideoCall ? "Starting Video Call" : "Starting Call",
        description: `Connecting ${isVideoCall ? 'video ' : ''}call with ${contactName}...`,
      });

      // Portal context must be exact: '/business/customers' contains the substring 'customer', so a
      // loose includes('/customer') check would wrongly route staff into the customer portal and bounce
      // them to the customer login page.
      const isCustomerContext = location.pathname.startsWith('/customer');
      const voipPath = isCustomerContext ? '/customer/crm-voip' : '/business/crm-voip';

      navigate(voipPath, {
        state: {
          appUserId: contact.app_user_id,   // WebRTC target = app user id, NOT the phone number
          businessName: contactName,
          isVideoCall: !!isVideoCall,
          autoCall: true,
        },
      });
    } catch (error) {
      toast({
        title: isVideoCall ? "Video Call Failed" : "Call Failed",
        description: "Failed to open the caller",
        variant: "destructive",
      });
    }
  };

  const handleCallContact = (contact) => startContactCall(contact, false);

  const handleVideoCallContact = (contact) => startContactCall(contact, true);

  // Load contacts on component mount
  useEffect(() => {
    if (user) {
      loadContacts();
    }
  }, [user]);

  // Filter contacts based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredContacts(contacts);
    } else {
      const filtered = contacts.filter(contact => 
        contact.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone_number?.includes(searchTerm) ||
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContacts(filtered);
    }
  }, [searchTerm, contacts]);

  const loadContacts = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await makeAuthenticatedRequest('/api/v1/contacts/', {
        method: 'GET'
      });

      if (response.status >= 500) {
        // Backend unavailable for this session type — show empty state, not error
        setContacts([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load contacts (${response.status})`);
      }

      const contactsData = await response.json();
      setContacts(Array.isArray(contactsData) ? contactsData : []);

    } catch (error) {
      console.error('Error loading contacts:', error);
      // Network/CORS errors (often caused by 5xx with missing CORS headers) —
      // show empty state rather than a confusing error for customer sessions.
      if (!error.message || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        setContacts([]);
        return;
      }
      setError(error.message || 'Failed to load contacts');
      toast({
        title: "Error Loading Contacts",
        description: error.message || 'Failed to load contacts',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // D14 — reset the import workflow to a clean state.
  const resetImport = () => {
    setImportText(''); setImportPreview(null); setImportPhase('idle');
    setImportResult(null); setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Single canonical validation path for BOTH file and paste input.
  const runParse = (text) => {
    setImportPhase('validating');
    const preview = parseContactsCsv(text);
    setImportPreview(preview);
    setImportPhase(preview.rows.length > 0 ? 'ready' : 'issues');
  };

  const handleImportFile = (e) => {
    const file = e.target.files && e.target.files[0];
    setFileError(''); setImportResult(null);
    if (!file) return;
    if (file.size > CSV_MAX_FILE_BYTES) {
      setFileError(`File is too large. Maximum ${Math.round(CSV_MAX_FILE_BYTES / (1024 * 1024))} MB.`);
      setImportPreview(null); setImportPhase('failed'); return;
    }
    setImportPhase('reading');
    const reader = new FileReader();
    reader.onerror = () => { setFileError("We couldn't read that file. Please try again."); setImportPhase('failed'); };
    reader.onload = () => {
      const text = String(reader.result || '');
      // Guard the row count before building a huge preview.
      const lineCount = text.split(/\r\n|\r|\n/).filter((l) => l.trim()).length;
      if (lineCount > CSV_MAX_ROWS + 1) {
        setFileError(`Too many rows. Maximum ${CSV_MAX_ROWS.toLocaleString()} contacts per import.`);
        setImportPreview(null); setImportPhase('failed'); return;
      }
      setImportText(text);
      runParse(text);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handlePasteChange = (text) => {
    setImportText(text); setFileError(''); setImportResult(null);
    if (text.trim()) runParse(text); else { setImportPreview(null); setImportPhase('idle'); }
  };

  const handleImportContacts = async () => {
    // Duplicate-submit prevention: only proceed from a validated 'ready' state with valid rows.
    if (importPhase === 'importing' || importPhase === 'complete') return;
    const contactsData = (importPreview && importPreview.rows) || [];
    if (contactsData.length === 0) { setError('No valid contacts to import. Please check the file/format.'); return; }

    try {
      setImportPhase('importing'); setError('');
      const response = await makeAuthenticatedRequest('/api/v1/contacts/import', {
        method: 'POST',
        body: JSON.stringify({ contacts: contactsData, source: 'web_csv_import' }),
      });
      if (!response.ok) throw new Error(`Import failed (${response.status})`);
      let result = {};
      try { result = await response.json(); } catch { result = { successful_imports: contactsData.length }; }
      setImportResult(result);
      await loadContacts();
      const failed = Number(result.failed_imports || 0);
      setImportPhase(failed > 0 ? 'issues' : 'complete');
      toast({
        title: failed > 0 ? 'Import completed with issues' : 'Import completed',
        description: `${result.successful_imports ?? contactsData.length} added`
          + (result.duplicate_contacts ? ` · ${result.duplicate_contacts} already existed` : '')
          + (failed ? ` · ${failed} could not be imported` : ''),
      });
    } catch (error) {
      console.error('Error importing contacts:', error);   // raw detail to logs only
      setImportPhase('failed');
      setError('We couldn’t import your contacts right now. Please try again.');
      toast({ title: 'Import error', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleSendInvite = async () => {
    if (!invitePhone.trim()) {
      setError('Please enter a phone number');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const defaultMessage = "Join us on Servisfy platform for seamless communication and better customer service experience. Download the app and connect with our team instantly!";
      const finalMessage = inviteMessage.trim() || defaultMessage;

      const response = await makeAuthenticatedRequest('/api/v1/contacts/invite', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: invitePhone,
          invitation_type: 'customer',
          message: finalMessage
        })
      });

      if (!response.ok) {
        throw new Error(`Invitation failed (${response.status})`);
      }

      setShowInvite(false);
      setInvitePhone('');
      setInviteMessage('');
      
      toast({
        title: "Invitation Sent",
        description: `Servisfy invitation sent to ${invitePhone} successfully!`,
      });
      
    } catch (error) {
      console.error('Error sending invite:', error);
      setError(error.message || 'Failed to send invitation');
      toast({
        title: "Invitation Failed",
        description: error.message || 'Failed to send invitation. Please try again.',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits && !value.startsWith('+')) {
      return '+' + digits;
    }
    return value;
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">Please log in to manage contacts</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 p-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Contact Manager
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setShowInvite(true)}
                size="sm"
                variant="default"
                data-testid="add-contact-btn"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Add Contact
              </Button>
              <Badge variant="outline">
                {contacts.length} contacts
              </Badge>
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="contacts" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="contacts">My Contacts</TabsTrigger>
              <TabsTrigger value="invite">Invite Friends</TabsTrigger>
            </TabsList>

            {/* My Contacts Tab */}
            <TabsContent value="contacts" className="space-y-4">
              <div className="flex space-x-2 mb-4">
                <Button
                  onClick={() => setShowImport(true)}
                  variant="outline"
                  size="sm"
                  data-testid="import-contacts-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
                
                <Button
                  onClick={() => setShowInvite(true)}
                  variant="outline"
                  size="sm"
                  data-testid="invite-contact-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Invite
                </Button>
                
                <Button
                  onClick={loadContacts}
                  variant="outline"
                  size="sm"
                  disabled={isLoading}
                  data-testid="refresh-contacts-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-contacts-input"
                />
              </div>

              {/* Error Display */}
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {/* Import Modal — D14: file OR paste, one canonical parser, preview before mutation, honest phases. */}
              {showImport && (
                <Card className="max-h-[80vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle>Import Contacts</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-2" role="tablist" aria-label="Import method">
                      <Button size="sm" variant={importMode === 'file' ? 'default' : 'outline'}
                        onClick={() => { setImportMode('file'); }} role="tab" aria-selected={importMode === 'file'}>
                        Upload CSV file
                      </Button>
                      <Button size="sm" variant={importMode === 'paste' ? 'default' : 'outline'}
                        onClick={() => { setImportMode('paste'); }} role="tab" aria-selected={importMode === 'paste'}>
                        Paste CSV
                      </Button>
                    </div>

                    {importMode === 'file' ? (
                      <div>
                        <Label htmlFor="importFile">CSV file</Label>
                        <input id="importFile" ref={fileInputRef} type="file" accept=".csv,text/csv"
                          onChange={handleImportFile}
                          className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-purple-100 file:text-purple-800 hover:file:bg-purple-200" />
                        <p className="text-xs text-gray-500 mt-1">
                          Up to {Math.round(CSV_MAX_FILE_BYTES / (1024 * 1024))} MB · {CSV_MAX_ROWS.toLocaleString()} rows.
                          Columns: <span className="font-medium">name</span>, then <span className="font-medium">phone</span> and/or <span className="font-medium">email</span>.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <Label htmlFor="importText">Paste CSV</Label>
                        <Textarea id="importText"
                          placeholder="name,phone,email — one contact per line (a header row is optional)"
                          value={importText} onChange={(e) => handlePasteChange(e.target.value)} rows={6} />
                        <p className="text-xs text-gray-500 mt-1">Example: <code>{CSV_TEMPLATE.split('\n')[1]}</code></p>
                      </div>
                    )}

                    {fileError ? <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fileError}</p> : null}

                    {/* Preview before any mutation */}
                    {importPreview ? (
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm space-y-1" aria-live="polite">
                        <div className="font-medium text-gray-800">Preview</div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                          <span>Total rows: <b>{importPreview.total}</b></span>
                          <span className="text-green-700">Ready: <b>{importPreview.rows.length}</b></span>
                          {importPreview.invalid.length > 0 && <span className="text-amber-700">Invalid: <b>{importPreview.invalid.length}</b></span>}
                          {importPreview.duplicatesInFile > 0 && <span className="text-gray-500">Duplicate in file: <b>{importPreview.duplicatesInFile}</b></span>}
                        </div>
                        {importPreview.ignoredColumns && importPreview.ignoredColumns.length > 0 && (
                          <div className="text-xs text-gray-500">Ignored columns (tenant scope is server-controlled): {importPreview.ignoredColumns.join(', ')}</div>
                        )}
                        {importPreview.invalid.length > 0 && (
                          <ul className="text-xs text-amber-700 list-disc pl-5 max-h-24 overflow-y-auto">
                            {importPreview.invalid.slice(0, 8).map((iv) => <li key={iv.row}>Row {iv.row}: {iv.reason}</li>)}
                            {importPreview.invalid.length > 8 && <li>…and {importPreview.invalid.length - 8} more</li>}
                          </ul>
                        )}
                      </div>
                    ) : null}

                    {importResult ? (
                      <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800" aria-live="polite">
                        Added {importResult.successful_imports ?? 0}
                        {importResult.duplicate_contacts ? ` · ${importResult.duplicate_contacts} already existed` : ''}
                        {importResult.failed_imports ? ` · ${importResult.failed_imports} failed` : ''}.
                      </div>
                    ) : null}

                    <div className="flex items-center gap-2">
                      <Button onClick={handleImportContacts}
                        disabled={importPhase === 'importing' || importPhase === 'complete' || !(importPreview && importPreview.rows.length > 0)}
                        data-testid="confirm-import-btn">
                        {importPhase === 'importing' ? 'Importing…'
                          : importPhase === 'complete' ? 'Imported'
                          : `Import ${importPreview ? importPreview.rows.length : ''} contact${importPreview && importPreview.rows.length === 1 ? '' : 's'}`}
                      </Button>
                      <Button variant="outline" onClick={() => { resetImport(); setShowImport(false); }}>
                        {importPhase === 'complete' ? 'Close' : 'Cancel'}
                      </Button>
                      <span className="text-xs text-gray-400 ml-auto">
                        {{ reading: 'Reading file…', validating: 'Validating…', ready: 'Ready to import',
                           importing: 'Importing…', complete: 'Complete', issues: 'Review issues above', failed: 'Failed' }[importPhase] || ''}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Invite Modal */}
              {showInvite && (
                <Card className="max-h-[80vh] overflow-y-auto">
                  <CardHeader>
                    <CardTitle>Send Invitation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="invitePhone">Phone Number</Label>
                      <Input
                        id="invitePhone"
                        type="tel"
                        placeholder="Phone number (with country code)"
                        value={invitePhone}
                        onChange={(e) => setInvitePhone(formatPhoneNumber(e.target.value))}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="inviteMessage">Custom Message (Optional)</Label>
                      <Textarea
                        id="inviteMessage"
                        placeholder="Join us on Servisfy platform for seamless communication..."
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button onClick={handleSendInvite} disabled={isLoading} data-testid="confirm-invite-btn">
                        {isLoading ? 'Sending...' : 'Send Invitation'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowInvite(false)}>
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contacts List */}
              {isLoading && !contacts.length ? (
                <p className="text-center text-gray-500 py-4">Loading contacts...</p>
              ) : filteredContacts.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  {searchTerm ? 'No contacts match your search' : 'No contacts found. Import some contacts to get started.'}
                </p>
              ) : (
                <div className="space-y-3">
                  {filteredContacts.map((contact, index) => (
                    <div
                      key={contact.id || index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                      data-testid={`contact-row-${contact.id || index}`}
                    >
                      <div className="flex-1">
                        <h4 className="font-medium">
                          {contact.display_name || `${contact.first_name} ${contact.last_name}`.trim()}
                        </h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          {contact.phone_number && (
                            <div className="flex items-center">
                              <Phone className="w-3 h-3 mr-1" />
                              {contact.phone_number}
                            </div>
                          )}
                          {contact.email && (
                            <div className="flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {contact.email}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {contact.is_app_user && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            App User
                          </Badge>
                        )}
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleStartConversation(contact)}
                          title="Start conversation"
                          data-testid={`chat-contact-${contact.id || index}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                        </Button>
                        
                        {contact.phone_number && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleCallContact(contact)}
                              title="Call contact"
                              data-testid={`call-contact-${contact.id || index}`}
                            >
                              <Phone className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleVideoCallContact(contact)}
                              title="Video call contact"
                              data-testid={`video-contact-${contact.id || index}`}
                            >
                              <Video className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Invite Friends Tab */}
            <TabsContent value="invite">
              <FriendInvitation userRole={user?.role || 'customer'} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Real staff-to-staff direct chat (same workflow as Voice Calls). Opened by the contact-row
          chat icon only for contacts the backend resolved to same-business staff (is_app_user). */}
      <StaffChatModal
        open={!!staffChatPeer}
        peer={staffChatPeer}
        businessId={user?.brandId || user?.business_id}
        selfUid={auth?.currentUser?.uid || user?.uid || user?.id}
        makeAuthenticatedRequest={makeAuthenticatedRequest}
        onClose={() => setStaffChatPeer(null)}
      />
    </div>
  );
};

export default ContactManager;